import { ReactElement, useEffect, ChangeEvent, useState } from "react";
import { IProductionExtended, IState, IStrandProduction } from "../../../types";
import { Paper, Tab, Tabs } from "@mui/material";
import TabPanel from "../../../components/tabPanel";
import { useSelector } from "react-redux";
import HeatInformation from "./heatInformation";
import LRFHeatInformation from "../../meltShop/lrf/lrfHeatInformation";
import CasterHeatInformation from "../../meltShop/caster/casterHeatInformation";
import ProductionInformation from "../../mill/components/productionInformation";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary } from "../../../components/accordion";

interface IHeatInformationProps {
  productionItems: IProductionExtended[];
  height?: number;
}

interface ITreeMap {
  children: Record<string,  ITreeMap>;
  data: IProductionExtended[];
}

interface IChildComponentProps {
  node: ITreeMap;
  darkMode: boolean;
}

// const Accordion = withStyles({
//   root: {
//     border: "1px solid rgba(0, 0, 0, .125)",
//     boxShadow: "none",
//     "&:not(:last-child)": {
//       borderBottom: 0,
//     },
//     "&:before": {
//       display: "none",
//     },
//     "&$expanded": {
//       margin: "auto",
//     },
//   },
//   expanded: {},
// })(MuiAccordion);
//
// const AccordionSummary = withStyles({
//   root: {
//     backgroundColor: "rgba(0, 0, 0, .03)",
//     borderBottom: "1px solid rgba(0, 0, 0, .125)",
//     marginBottom: -1,
//     minHeight: 56,
//     "&$expanded": {
//       minHeight: 56,
//     },
//   },
//   content: {
//     "&$expanded": {
//       margin: "12px 0",
//     },
//   },
//   expanded: {},
// })(MuiAccordionSummary);
//
// const AccordionDetails = withStyles(() => ({
//   root: {
//     padding: 0, //theme.spacing(2),
//     height: 500,
//     overflowY: "auto"
//   },
// }))(MuiAccordionDetails);


interface IContentComponentProps {
  productionItem: IProductionExtended;
}

const ContentComponent = ({ productionItem }: IContentComponentProps): ReactElement | null=> {

  if (productionItem.__typename === "EAFProduction") {
    return <HeatInformation productionItem={productionItem} height={500}/>;
  }
  if (productionItem.__typename === "LRFProduction") {
    return <LRFHeatInformation productionItem={productionItem} height={500}/>;
  }

  if (productionItem.__typename === "CasterProduction") {
    return <CasterHeatInformation productionItem={productionItem} height={500}/>;
  }

  if (productionItem.__typename === "StrandProduction") {
    return <ProductionInformation productionItem={productionItem as IStrandProduction} height={500}/>;
  }

  return null;
};

const ChildComponent = ({ node, darkMode }: IChildComponentProps ): ReactElement => {
  const [tab, setTab] = useState("");
  const [expanded, setExpanded] = useState<string | false>(false);

  useEffect(() => {
    setTab(Object.keys(node.children)[0]);
  }, [ node ]);

  const handleChange = (_event: ChangeEvent<any>, newValue: string) => {
    setTab(newValue);
    setExpanded(false);
  };

  return (
    <Paper>
      <Tabs
        value={tab}
        onChange={handleChange}
        indicatorColor="primary"
        textColor="primary"
        centered={true}
        style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
        { Object.keys(node.children).map(k => <Tab key={k} label={k} value={k}/>) }
      </Tabs>
      { Object.entries(node.children).map(([k, v], index) => {
        const key = `${k}-${index}`;

        if (v.data && v.data.length > 1) {
          return (
            <TabPanel key={key} value={tab} index={k}>
              { v.data && v.data.map((productionItem, i) => {
                const prodKey = `${key}-prod-${i}`;

                return (
                  <Accordion key={prodKey}  square={true} expanded={expanded === prodKey} onChange={() => setExpanded(expanded === prodKey ? false : prodKey)}>
                    <AccordionSummary aria-controls="panel1d-content" id="panel1d-header" expandIcon={<ExpandMoreIcon/>}>
                      <strong>{`${productionItem.reference} - ${productionItem.machine.name}`}</strong>
                    </AccordionSummary>
                    <AccordionDetails>
                      <ContentComponent productionItem={productionItem}/>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </TabPanel>
          );
        }

        return (
          <TabPanel key={key} value={tab} index={k}>
            { v.data && v.data.map((productionItem, i) => {
              return <ContentComponent  key={`${key}-prod-${i}`} productionItem={productionItem}/>;
            })}
          </TabPanel>
        );
      }) }
    </Paper>
  );
};

const FullInformation = ({ productionItems, height }: IHeatInformationProps): ReactElement => {
  const darkMode = useSelector((state: IState) => state.darkMode);
  const [tab, setTab] = useState("");
  const [treeMap, setTreeMap] = useState<Record<string,  ITreeMap> | null>(null);

  useEffect(() => {
    const newTree: Record<string,  ITreeMap> = {};
    const ordered =  productionItems.sort((a, b) => a.beginDatetime - b.beginDatetime);
    let tab1 = "";

    ordered.forEach(item => {

      if (!newTree[item.area.name]) {
        newTree[item.area.name] = {
          children: {},
          data: []
        };

        if (tab1 === "") {
          tab1 = item.area.name;
        }
      }

      if (!newTree[item.area.name].children[item.zone.name]) {
        newTree[item.area.name].children[item.zone.name] = {
          children: {},
          data: [ item ]
        };
      } else {
        newTree[item.area.name].children[item.zone.name].data.push(item);
      }


    });

    setTreeMap(newTree);
    setTab(tab1);
  }, [productionItems]);


  const handleChange = (_event: ChangeEvent<any>, newValue: string) => {
    setTab(newValue);
  };

  return (
    <div style={{ height }}>
      <Paper>
        <Tabs
          value={tab}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          centered={true}
          style={{ background: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)" }}>
          { treeMap && Object.keys(treeMap).map(k => <Tab key={k} label={k} value={k}/>) }
        </Tabs>
        { treeMap && Object.entries(treeMap).map(([k, v], index) => {
          const key = `${k}-${index}`;

          return (
            <TabPanel key={key} value={tab} index={k}>
              <ChildComponent node={v} darkMode={darkMode}/>
            </TabPanel>
          );
        }) }
      </Paper>
    </div>
  );
};

export default FullInformation;
